import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  Package,
  Boxes,
  Wrench,
  Truck,
  Target,
  Mail,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Building2,
  Menu,
  X,
  Zap,
  Sliders,
  Handshake,
  Megaphone,
  GitCompare,
} from 'lucide-react';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Tableau de bord', roles: ['super_admin', 'admin_franchise', 'commercial', 'technician', 'read_only'] },
  { path: '/suppliers', icon: Building2, label: 'Fournisseurs', roles: ['super_admin', 'admin_franchise', 'commercial', 'technician', 'read_only'] },
  { path: '/products', icon: Package, label: 'Catalogue', roles: ['super_admin', 'admin_franchise', 'commercial', 'technician', 'read_only'] },
  { path: '/clients', icon: Users, label: 'Clients', roles: ['super_admin', 'admin_franchise', 'commercial', 'technician', 'read_only'] },
  { path: '/inventory', icon: Boxes, label: 'Inventaire', roles: ['super_admin', 'admin_franchise', 'technician'] },
  { path: '/sav', icon: Wrench, label: 'SAV', roles: ['super_admin', 'admin_franchise', 'technician'] },
  { path: '/loans', icon: Truck, label: 'Prets', roles: ['super_admin', 'admin_franchise', 'technician'] },
  { path: '/prospecting', icon: Target, label: 'Prospection', roles: ['super_admin', 'admin_franchise', 'commercial'] },
  { path: '/emails', icon: Mail, label: 'Emails', roles: ['super_admin', 'admin_franchise', 'commercial'] },
  { path: '/marketing', icon: Megaphone, label: 'Marketing', roles: ['super_admin', 'admin_franchise', 'commercial'] },
  { path: '/exploitation', icon: Zap, label: 'Exploitation', roles: ['super_admin', 'admin_franchise', 'technician'] },
  { path: '/reseau', icon: Handshake, label: 'Reseau', roles: ['super_admin', 'admin_franchise', 'commercial'] },
  { path: '/admin/configurateur', icon: Sliders, label: 'Configurateur', roles: ['super_admin', 'admin_franchise', 'commercial'] },
  { path: '/comparateur', icon: GitCompare, label: 'Comparateur', roles: ['super_admin', 'admin_franchise', 'commercial'] },
  { path: '/settings', icon: Settings, label: 'Parametres', roles: ['super_admin', 'admin_franchise'] },
];

const Sidebar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const filteredNavItems = navItems.filter(item => 
    item.roles?.includes(user?.role) ?? true
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path || 
      (item.path !== '/' && location.pathname.startsWith(item.path));

    if (item.external) {
      return (
        <a
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setMobileOpen(false)}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group text-[#8C8F94] hover:text-white hover:bg-white/5`}
          data-testid={`nav-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
        >
          <item.icon size={20} className="group-hover:text-[#00E01A]" />
          {!collapsed && <span className="text-sm">{item.label}</span>}
        </a>
      );
    }

    return (
      <Link
        to={item.path}
        onClick={() => setMobileOpen(false)}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
          isActive
            ? 'bg-[#00E01A] text-black font-semibold shadow-[0_0_15px_rgba(0,224,26,0.3)]'
            : 'text-[#8C8F94] hover:text-white hover:bg-white/5'
        }`}
        data-testid={`nav-${item.path.replace('/', '') || 'dashboard'}`}
      >
        <item.icon size={20} className={isActive ? '' : 'group-hover:text-[#00E01A]'} />
        {!collapsed && <span className="text-sm">{item.label}</span>}
      </Link>
    );
  };

  const SidebarContent = () => (
    <>
      {/* Logo */}
      <div className={`p-4 border-b border-[#2C3338] ${collapsed ? 'px-3' : ''}`}>
        <Link to="/" className="flex items-center gap-3">
          <img
            src="https://www.riding-watt.com/wp-content/uploads/2024/01/logo-ridingwatt-v2.png"
            alt="Riding Watt"
            className={`transition-all duration-200 ${collapsed ? 'h-8' : 'h-10'}`}
          />
          {!collapsed && (
            <div className="hidden lg:block">
              <p className="text-white font-semibold text-sm font-['Outfit']">Riding Watt</p>
              <p className="text-[#8C8F94] text-xs">CRM</p>
            </div>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {filteredNavItems.map((item) => (
          <NavLink key={item.path} item={item} />
        ))}
      </nav>

      {/* User Info & Logout */}
      <div className="p-3 border-t border-[#2C3338]">
        {!collapsed && (
          <div className="px-3 py-2 mb-2">
            <p className="text-white text-sm font-medium truncate">
              {user?.first_name} {user?.last_name}
            </p>
            <p className="text-[#8C8F94] text-xs truncate">
              {user?.role === 'super_admin' && 'Super Admin'}
              {user?.role === 'admin_franchise' && 'Admin'}
              {user?.role === 'commercial' && 'Commercial'}
              {user?.role === 'technician' && 'Technicien'}
              {user?.role === 'read_only' && 'Lecture seule'}
            </p>
            {user?.franchise_name && (
              <p className="text-[#00E01A] text-xs truncate mt-0.5">
                {user.franchise_name}
              </p>
            )}
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-[#8C8F94] hover:text-[#E74C3C] hover:bg-[#2A1515]/50 transition-colors ${
            collapsed ? 'justify-center' : ''
          }`}
          data-testid="logout-btn"
        >
          <LogOut size={20} />
          {!collapsed && <span className="text-sm">Déconnexion</span>}
        </button>
      </div>

      {/* Collapse Button (Desktop) */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden lg:flex absolute -right-3 top-20 w-6 h-6 bg-[#1D2327] border border-[#2C3338] rounded-full items-center justify-center text-[#8C8F94] hover:text-white hover:border-[#00E01A] transition-colors"
        data-testid="sidebar-collapse-btn"
      >
        {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
      </button>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#141617] border-b border-[#2C3338] z-40 flex items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2">
          <img
            src="https://www.riding-watt.com/wp-content/uploads/2024/01/logo-ridingwatt-v2.png"
            alt="Riding Watt"
            className="h-8"
          />
          <span className="text-white font-semibold font-['Outfit']">CRM</span>
        </Link>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 text-[#8C8F94] hover:text-white transition-colors"
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed top-16 left-0 bottom-0 w-64 bg-[#11100D] border-r border-[#2C3338] z-50 transform transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <SidebarContent />
        </div>
      </aside>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex fixed top-0 left-0 bottom-0 bg-[#11100D] border-r border-[#2C3338] z-30 flex-col transition-all duration-300 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        <SidebarContent />
      </aside>

      {/* Spacer for content */}
      <div className={`hidden lg:block ${collapsed ? 'w-16' : 'w-64'} flex-shrink-0 transition-all duration-300`} />
      <div className="lg:hidden h-16" />
    </>
  );
};

export default Sidebar;
